import { useCallback, useEffect, useMemo, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Geometry, MultiPolygon, Polygon } from "@turf/helpers";
import { twMerge } from "tailwind-merge";
import cn from "classnames";
import { Button, DeprecatedTooltip, Loading, Theme } from "@landtechnologies/components";
import { LogEventCreatedSiteEventLocation } from "react-migration/lib/util/logEventV2";
import { filterSitesInGeofence } from "react-migration/domains/geofencing/utils/filterSitesInGeofence";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import type { SaveSiteLayer, Site } from "../../typings";
import { MergeSiteModal } from "../MergeSiteModal";
import { StageSelect } from "./StageSelect";
import { CreatedSite, useSaveSiteMutation } from "./useSaveSiteMutation/useSaveSiteMutation";
import { useUpdateSiteMutation } from "./useUpdateSiteMutation";
import { useCheckStages } from "./useCheckStages";
import { useIntersectingSitesInViewport } from "react-migration/layouts/map/Multilayer/layer_types/SitesLayerType/useIntersectingSitesInViewport";
import { mergeSiteGeometries } from "react-migration/domains/sites/helpers/mergeSiteGeometries";
import { Nullable } from "react-migration/domains/property/typings";

export function isValidSiteGeometry(geom?: Nullable<Geometry>): geom is MultiPolygon | Polygon {
  if (!geom) return false;
  if (geom.type !== "Polygon" && geom.type !== "MultiPolygon") return false;
  return true;
}

export interface SaveSiteButtonProps {
  layer: SaveSiteLayer;
  source: LogEventCreatedSiteEventLocation;
  text?: string;
  siteGeometry?: GeoJSON.Point | GeoJSON.Polygon | GeoJSON.MultiPolygon;
  planning_alert_result?: string;
  handleSaveSites?: (savedSites: CreatedSite[]) => void;
  assignees?: string[];
  tidbit?: {
    planning_application: {
      ref: string;
      gss_code: string;
    };
  };
  coordId?: string;
  title?: string;
  disableDryRun?: boolean;
  siteMatchType?: "EXACT" | "INTERSECTS_POINT";
  mergeSiteOnSave?: boolean;
}

export function SaveSiteButton({
  handleSaveSites,
  layer,
  source,
  siteGeometry,
  text,
  planning_alert_result,
  assignees,
  tidbit,
  coordId,
  title,
  disableDryRun,
  siteMatchType,
  mergeSiteOnSave = false,
}: SaveSiteButtonProps) {
  const { t } = useTranslation();
  const [isInsideGeo, setIsInsideGeo] = useState(true);
  const [showStageSelectPopover, setShowStageSelectPopover] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [hasMergeError, setHasMergeError] = useState(false);
  const intersectingSites = useIntersectingSitesInViewport(siteGeometry);

  const {
    data,
    loading,
    selectedStageId,
    setSelectedStageId,
    stages,
    isStagesLoading,
    networkStatus,
  } = useCheckStages({
    sites: [
      {
        geometry: siteGeometry!,
        source: { layer, planning_alert_result, title },
        assignees,
        tidbit,
        title,
        id: coordId,
      },
    ],
    siteMatchType,
    disableDryRun,
  });

  const savable = useMemo(
    () => !!(selectedStageId && siteGeometry),
    [selectedStageId, siteGeometry]
  );

  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [updateSite, { loading: isUpdating }] = useUpdateSiteMutation();
  const [saveSite, creatingSiteState] = useSaveSiteMutation({
    layer,
    source,
    sites: [
      {
        geometry: siteGeometry!,
        source: { layer, planning_alert_result, title },
        title,
        assignees,
        tidbit,
        id: coordId,
      },
    ],
    stageId: selectedStageId,
  });

  const disabled = useMemo(
    () => creatingSiteState.loading || !savable || !isInsideGeo || isUpdating,
    [creatingSiteState, savable, isInsideGeo, isUpdating]
  );

  useEffect(() => {
    const sitesInsideGeo = filterSitesInGeofence([{ geometry: siteGeometry }]);
    if (!sitesInsideGeo.length) {
      setIsInsideGeo(false);
    }
  }, [siteGeometry]);

  const updateExistingSiteStage = async (existingSite: Site) =>
    updateSite({
      variables: {
        updateData: {
          _stage: selectedStageId,
          _id: existingSite?._id,
        },
      },
    });

  const saveNewSite = async () => {
    try {
      const saveSiteResponse = await saveSite();
      const savedSites = saveSiteResponse?.data?.createSites;

      setIsSaved(true);

      savedSites && handleSaveSites?.(savedSites.map((x) => x.saved_site));
    } catch (error) {
      setIsSaved(false);
    }
  };

  const mergeWithSite = useCallback(
    async (siteId: string) => {
      setHasMergeError(false);

      try {
        const siteToMerge = intersectingSites.find((s) => s.id === siteId);
        const siteToMergeGeometry = siteToMerge?.geometry;

        if (
          !siteToMerge ||
          !isValidSiteGeometry(siteGeometry) ||
          !isValidSiteGeometry(siteToMergeGeometry)
        )
          return;

        const mergedSiteGeometry = mergeSiteGeometries(siteGeometry, siteToMergeGeometry).geometry;

        if (!mergedSiteGeometry) throw new Error("Failed to merge site geometries.");

        const { data } = await updateSite({
          variables: {
            updateData: {
              _id: siteToMerge.id,
              title: siteToMerge.properties.title,
              geometry: mergedSiteGeometry,
            },
          },
        });

        const updatedSite = data?.updateSite.site;

        updatedSite && handleSaveSites?.([updatedSite]);
        setIsSaved(true);
      } catch (error) {
        if (error instanceof Error) console.error(error.message);
        else console.error(`Encountered a problem merging with site (id: ${siteId}).`);
        setHasMergeError(true);

        setIsSaved(false);
      }

      setShowStageSelectPopover(false);
    },
    [handleSaveSites, intersectingSites, siteGeometry, updateSite]
  );

  const onSaveHandler = () => {
    setShowStageSelectPopover(false);

    const existingSavedSite = data?.createSitesDryRun[0].saved_site;

    if (existingSavedSite) {
      updateExistingSiteStage(existingSavedSite);
    } else if (mergeSiteOnSave && intersectingSites.length) {
      setShowMergeModal(true);
    } else {
      saveNewSite();
    }
  };

  if (!siteGeometry) return null;

  const saveBtnClasses = twMerge(
    "atlas-px-3 atlas-tracking-wider atlas-flex-grow atlas-text-white atlas-bg-[#ee64b3]",
    disabled && "atlas-bg-background-disabled-light"
  );

  // Note: Dynamic colours used for buttons here to match legacy component
  // until the legacy save site component is replaced - use pink-500/700 after
  return (
    <>
      <DeprecatedTooltip
        title={
          isInsideGeo
            ? t("sites.save_site.save_site_dropdown.save")
            : t("sites.save_site.save_site.search_results_outside_of_geofence")
        }
        placement="bottom"
        theme={Theme.Dark}
        disabled={isInsideGeo}
      >
        <Popover.Root open={showStageSelectPopover} onOpenChange={setShowStageSelectPopover}>
          <Popover.Anchor className="atlas-border atlas-border-white atlas-text-[#ee64b3] atlas-text-xs atlas-h-8 atlas-inline-flex atlas-justify-between atlas-rounded atlas-overflow-hidden">
            {!(isSaved || data?.createSitesDryRun[0].saved_site) ? (
              <button
                disabled={disabled || showMergeModal}
                onClick={onSaveHandler}
                className={saveBtnClasses}
                data-testid="save-site-outer"
              >
                {creatingSiteState.loading || loading ? (
                  <Loading />
                ) : (
                  text || t("sites.save_site.save_site_dropdown.save")
                )}
              </button>
            ) : (
              <Button.Secondary disabled>
                {creatingSiteState.loading || loading ? (
                  <Loading />
                ) : (
                  t("sites.save_site.save_site_dropdown.saved")
                )}
              </Button.Secondary>
            )}
            <Popover.Trigger
              disabled={disabled || showMergeModal}
              className={cn(
                "atlas-bg-white active:atlas-bg-white focus:atlas-bg-neutral-50 hover:atlas-text-[#d0529a] focus:atlas-text-[#d0529a] atlas-w-5",
                { "atlas-hidden": creatingSiteState.loading }
              )}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <i
                className={cn(
                  "atlas-w-5 atlas-text-base atlas-text-content-link icon-lt-arrow-down-s-fill"
                )}
              />
            </Popover.Trigger>
          </Popover.Anchor>
          <Popover.Portal forceMount>
            <Popover.Content
              className={cn(
                "atlas-flex atlas-flex-col atlas-w-60 atlas-z-50 atlas-gap-y-4 atlas-p-4 atlas-bg-white atlas-rounded atlas-border atlas-border-neutral-100 atlas-drop-shadow-xl",
                { "atlas-hidden": !showStageSelectPopover }
              )}
              align="center"
              forceMount
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <StageSelect
                size="xs"
                value={selectedStageId}
                onChange={setSelectedStageId}
                stages={stages || []}
                loading={isStagesLoading}
                networkStatus={networkStatus}
              />
              <div className="atlas-flex atlas-justify-between">
                <Button.Borderless
                  size="small"
                  className="atlas-px-0 atlas-font-normal"
                  onClick={() => setShowStageSelectPopover(false)}
                >
                  Cancel
                </Button.Borderless>
                <Button.Primary
                  size="small"
                  className="atlas-px-6 atlas-bg-[#ee64b3] focus:atlas-bg-[#d0529a] hover:atlas-bg-[#d0529a] atlas-font-normal"
                  onClick={onSaveHandler}
                  data-testid="save-site-inner"
                >
                  Save
                </Button.Primary>
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </DeprecatedTooltip>
      {showMergeModal && (
        <MergeSiteModal
          disabled={disabled}
          intersectingSites={intersectingSites}
          handleCancelSave={() => setShowMergeModal(false)}
          handleSaveAsSiteAs={saveNewSite}
          handleMergeSave={mergeWithSite}
          hasError={hasMergeError}
          setHasError={setHasMergeError}
        />
      )}
    </>
  );
}
